import React from 'react'
import { Link } from 'react-router-dom'

const sections = [
  {
    title: '1. Agreement to Terms',
    paragraphs: [
      'These Terms of Service (“Terms”) govern your access to and use of Employ Me’s website, dashboards, APIs, and related services (“Services”).',
      'By creating an account, booking a service, or otherwise using the Services, you agree to be bound by these Terms and all referenced policies.'
    ]
  },
  {
    title: '2. Eligibility',
    paragraphs: [
      'Users must be at least 18 years old and legally able to enter binding contracts. Hospitals and healthcare providers must maintain valid licenses and supply accurate regulatory documentation.',
      'Employ Me may request additional verification information at any time and may deny or revoke access if eligibility requirements are not met.'
    ]
  },
  {
    title: '3. Accounts & Security',
    paragraphs: [
      'You are responsible for safeguarding your credentials and for all activity that occurs under your account. Notify Employ Me immediately if you suspect unauthorized use.',
      'We reserve the right to suspend or terminate accounts that violate these Terms, provide false information, or compromise platform security.'
    ]
  },
  {
    title: '4. Permitted Use',
    paragraphs: [
      'Patients may post medical requests, communicate with hospitals, and make payments solely for lawful healthcare services.',
      'Hospitals must provide accurate quotations, honor accepted bookings, and comply with all applicable medical and data-protection regulations.',
      'Users must not scrape data, interfere with other accounts, upload malicious code, or reverse engineer any part of the Services.'
    ]
  },
  {
    title: '5. Payments, Fees, and Wallets',
    paragraphs: [
      'Employ Me facilitates payments through approved gateways and maintains wallet balances for hospitals. All fees and commissions are displayed clearly before payment.',
      'Completed payments are generally non-refundable. Disputes must be reported within 48 hours of delivery; Employ Me may freeze funds during investigations.',
      'We may delay or withhold payouts in cases of chargebacks, fraud alerts, or regulatory notice.'
    ]
  },
  {
    title: '6. Content & Intellectual Property',
    paragraphs: [
      'You retain ownership of content you submit but grant Employ Me a worldwide, royalty-free license to use, store, and display it for operating the Services.',
      'Employ Me logos, trademarks, and software are protected. You may not copy, modify, or distribute them without written consent.'
    ]
  },
  {
    title: '7. Data & Privacy',
    paragraphs: [
      'Use of the Services is subject to the Employ Me Privacy Policy. By using the platform you consent to the collection and processing of data described there.',
      'Hospitals receiving patient data must protect it, use it solely for fulfilling requests, and delete it when no longer required.'
    ]
  },
  {
    title: '8. Disclaimers',
    paragraphs: [
      'Employ Me provides the platform “as is” without warranties of any kind. We do not guarantee response times, medical outcomes, or uninterrupted availability.',
      'Medical advice and treatment are solely the responsibility of healthcare providers. Patients should verify hospital credentials and make informed decisions.'
    ]
  },
  {
    title: '9. Limitation of Liability',
    paragraphs: [
      'To the maximum extent permitted by law, Employ Me is not liable for indirect, incidental, special, or consequential damages arising from your use of the Services.',
      'Our aggregate liability will not exceed the total fees you paid to Employ Me during the three months preceding the event giving rise to the claim.'
    ]
  },
  {
    title: '10. Indemnification',
    paragraphs: [
      'You agree to indemnify and hold Employ Me harmless from claims, damages, or expenses arising out of your breach of these Terms, misuse of the Services, or violation of third-party rights.'
    ]
  },
  {
    title: '11. Termination',
    paragraphs: [
      'You may close your account at any time via settings or by contacting support. Employ Me may suspend or terminate access for violations, fraud, or legal requests.',
      'Sections relating to fees, intellectual property, confidentiality, disclaimers, and liability survive any termination.'
    ]
  },
  {
    title: '12. Governing Law & Dispute Resolution',
    paragraphs: [
      'These Terms are governed by the laws of the Federal Republic of Nigeria. Parties agree to attempt amicable resolution before pursuing legal action.',
      'Unless otherwise required by law, disputes will be handled in the courts of Lagos State.'
    ]
  },
  {
    title: '13. Changes to These Terms',
    paragraphs: [
      'We may update these Terms to reflect changes in laws, regulations, or product features. When material updates occur, we will provide notice through the platform or email.',
      'Continued use after changes become effective constitutes acceptance of the revised Terms.'
    ]
  },
  {
    title: '14. Contact Us',
    paragraphs: [
      'For questions about these Terms, contact legal@employmeglobal.com or write to Employ Me HQ, 42 Healthcare Drive, Lagos, Nigeria.'
    ]
  }
]

const TermsOfService = () => {
  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-10">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
        </div>

        <div className="rounded-2xl bg-white p-8 shadow-lg">
          <div className="mb-10 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-1 text-xs font-semibold text-gray-600">
              Updated {new Date().toLocaleDateString()}
            </div>
            <h1 className="mt-4 text-3xl font-bold text-gray-900">Employ Me Terms of Service</h1>
            <p className="mt-3 text-base text-gray-600">
              Please review these terms carefully before using Employ Me’s services or creating an account.
            </p>
          </div>

          <div className="space-y-8">
            {sections.map(section => (
              <section key={section.title}>
                <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                <div className="mt-3 space-y-3 text-sm text-gray-700 leading-relaxed">
                  {section.paragraphs.map((text, index) => (
                    <p key={index}>{text}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-10 rounded-xl bg-gray-50 p-6 text-sm text-gray-700">
            <h3 className="mb-2 font-semibold text-gray-900">Need help?</h3>
            <p>
              Reach out to our compliance team at{' '}
              <a href="mailto:legal@employmeglobal.com" className="text-blue-600 hover:underline">legal@employmeglobal.com</a>{' '}
              for any clarification about these Terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsOfService


